import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { isValidTimestamp } from '@eluve/date-utils';
import {
  OutgoingEmailTemplateTypesLookup,
  appointmentFileTypesSchema,
  platformTypesSchema,
  supportedApiBasedVendorsSchema,
  supportedVendorsSchema,
} from '@eluve/graphql-types';

const c = initContract();

const importAppointmentsRequestSchema = z.object({
  data: z.unknown(),
  vendor: supportedVendorsSchema,
  domain: z.string(),
  timezone: z.string().nullish(),
});

export type ImportAppointmentsRequest = z.infer<
  typeof importAppointmentsRequestSchema
>;

const prepareAppointmentFileUploadRequestSchema = z.object({
  uploadId: z.string(),
  extension: z.string(),
  fileType: appointmentFileTypesSchema,
  fileName: z.string(),
  fileSize: z.number(),
  fileHash: z.string(),
  origin: z.string().optional(),
});

export type PrepareAppointmentFileUploadRequest = z.infer<
  typeof prepareAppointmentFileUploadRequestSchema
>;

const markFileUploadCompleteRequestSchema = z.object({
  uploadId: z.string().uuid(),
  shouldBeTranscribed: z.boolean(),
  recordingStartedAt: z.string().refine(
    (val) => {
      return isValidTimestamp(val);
    },
    {
      message: 'Invalid date format',
    },
  ),
  recordingDuration: z.number().nullish(),
  wasSegmentDegraded: z.boolean(),
  chunkNum: z.number().nullish(),
  // TODO(grigor)[ENG-1746]: Clean up legacy chunked audio duration code after full migration
  skipDurationTrackingInTranscripts: z.boolean().nullish(),
});

export type MarkFileUploadCompleteRequest = z.infer<
  typeof markFileUploadCompleteRequestSchema
>;

export const supportedPdfTypesSchema = z.enum(['CHART', 'SUPERBILL']);
export type SupportedPdfTypes = z.infer<typeof supportedPdfTypesSchema>;

export const supportedAppointmentEmailTypesSchema = z.nativeEnum(
  OutgoingEmailTemplateTypesLookup,
);
export type SupportedAppointmentEmailTypes = z.infer<
  typeof supportedAppointmentEmailTypesSchema
>;

export const bulkEntityFiltersSchema = z.object({
  locationId: z.array(z.string()).optional(),
  statusLabel: z.array(z.string()).optional(),
  userId: z.array(z.string()).optional(),
  startDateFrom: z.string().optional(), // "YYYY-MM-DD" format
  startDateTo: z.string().optional(), // "YYYY-MM-DD" format
  patientId: z.array(z.string()).optional(),
});
export type BulkEntityFilters = z.infer<typeof bulkEntityFiltersSchema>;

const BulkEntitySelectionModeSchema = z.enum(['EXPLICIT', 'FILTER']);
export const BulkEntitySelectionMode = BulkEntitySelectionModeSchema.enum;
export type BulkEntitySelectionModeType = z.infer<
  typeof BulkEntitySelectionModeSchema
>;

export const appointmentsContract = c.router(
  {
    getAppointments: {
      method: 'GET',
      path: '/',
      responses: {
        200: z.array(
          z.object({
            appointmentId: z.string(),
            externalAppointmentId: z.string().nullish(),
            name: z.string(),
            description: z.string().nullish(),
            startDate: z.date(),
            endDate: z.date().nullish(),
          }),
        ),
      },
      query: z.object({
        startDate: z.string(),
        endDate: z.string(),
        status: z
          .array(z.enum(['NOT_STARTED', 'COMPLETED', 'ACTIVE', 'CANCELLED']))
          .default(['NOT_STARTED']),
        vendor: supportedVendorsSchema.optional(),
        domain: z.string().optional(),
      }),
      summary: 'Get appointments',
    },
    syncToEhr: {
      method: 'POST',
      path: ':appointmentId/sync-to-ehr',
      responses: {
        201: z.object({
          chartId: z.string(),
          chartUrl: z.string(),
        }),
        422: z.object({
          error: z.string(),
        }),
      },
      body: z.object({
        vendor: supportedApiBasedVendorsSchema,
        domain: z.string(),
        platform: platformTypesSchema.default('WEB'),
      }),
      summary: 'Sync summary to an external EHR',
    },
    create: {
      method: 'POST',
      path: '',
      responses: {
        201: z.object({
          id: z.string(),
          magicLink: z.string(),
        }),
      },
      headers: z.object({
        authorization: z.string(),
      }),
      body: z.object({
        vendor: supportedVendorsSchema,
        user: z.unknown(),
        appointment: z.unknown(),
      }),
      summary: 'Create new or get existing appointment',
    },
    importAppointmentsFromApi: {
      method: 'POST',
      path: 'api-import',
      responses: {
        201: z.array(
          z.object({
            id: z.string(),
          }),
        ),
      },
      body: z.object({
        vendor: supportedApiBasedVendorsSchema,
        domain: z.string(),
        data: z.object({
          startDate: z.number(),
          endDate: z.number(),
        }),
      }),
      summary: 'Import appointments from vendor API',
    },
    importAppointments: {
      method: 'POST',
      path: 'import',
      responses: {
        201: z.array(
          z.object({
            id: z.string(),
          }),
        ),
      },
      body: importAppointmentsRequestSchema,
      summary: 'Import appointments',
    },
    getOrCreatePdfUrl: {
      method: 'POST',
      path: ':appointmentId/pdf',
      responses: {
        201: z.object({
          pdfUrl: z.string(),
        }),
      },
      body: z.object({
        type: supportedPdfTypesSchema,
      }),
      summary: 'Get or create a PDF URL for an appointment',
    },
    createBulkPdfZipUrl: {
      method: 'POST',
      path: 'pdf/bulk',
      responses: {
        201: z.object({
          zipUrl: z.string(),
          totalCount: z.number(),
          successCount: z.number(),
          failedCount: z.number(),
          failedAppointmentIds: z.array(z.string()),
        }),
        422: z.object({
          message: z.string(),
        }),
      },
      body: z
        .object({
          // default to explicit mode if not provided to keep backwards compatibility
          selectionMode: BulkEntitySelectionModeSchema.default(
            BulkEntitySelectionMode.EXPLICIT,
          ),
          appointmentIds: z.array(z.string()).min(1).max(100).optional(),
          filters: bulkEntityFiltersSchema.optional(),
          type: supportedPdfTypesSchema.extract([
            // we only support bulk export of patient chart PDFs (for now)
            supportedPdfTypesSchema.enum.CHART,
          ]),
        })
        .refine(
          (data) => {
            if (data.selectionMode === BulkEntitySelectionMode.EXPLICIT) {
              return (
                data.appointmentIds !== undefined &&
                data.appointmentIds.length > 0
              );
            } else if (data.selectionMode === BulkEntitySelectionMode.FILTER) {
              return (
                data.filters !== undefined &&
                Object.keys(data.filters).length > 0 &&
                Object.values(data.filters).some(
                  (value) =>
                    value !== undefined &&
                    value !== null &&
                    (!Array.isArray(value) || value.length > 0),
                )
              );
            }
            return false;
          },
          {
            message:
              'Must provide non-empty appointmentIds for explicit mode or non-empty filters for filter mode',
          },
        ),
      summary:
        'Create a zip file URL for bulk export of appointment chart PDFs',
    },
    /**
     * Prepare a file upload for an appointment by creating necessary DB records
     * to track the attempt and then returning a presigned upload URL
     */
    prepareAppointmentFileUpload: {
      method: 'POST',
      path: ':appointmentId/file-upload',
      body: prepareAppointmentFileUploadRequestSchema,
      responses: {
        201: z.object({
          uploadId: z.string(),
          gcsFilePath: z.string(),
          uploadURL: z.string(),
          bytesUploaded: z.number().optional(),
          status: z.enum(['FILE_UPLOAD_REQUIRED']),
        }),
        200: z.object({
          status: z.enum(['FILE_ALREADY_EXISTS']),
          uploadId: z.string(),
        }),
      },
    },
    /**
     * Mark a file upload that we previously prepped as complete once the
     * file has been succesfully uploaded to GCS
     */
    markFileUploadComplete: {
      method: 'POST',
      path: ':appointmentId/file-upload/complete',
      body: markFileUploadCompleteRequestSchema,
      responses: {
        200: z.object({}),
      },
    },
    prepareAppointmentAudioRecords: {
      method: 'POST',
      path: ':appointmentId/prepare-audio-records',
      body: z.object({
        fileName: z.string(),
        useResumableUploads: z.boolean(),
        origin: z.string().optional(),
        platform: platformTypesSchema.optional(),
        platformVersion: z.string().optional(),
        platformId: z.string().optional(),
        userAgent: z.string().optional(),
        microphoneName: z.string().optional(),
      }),
      responses: {
        200: z.object({
          resumableUploadSessionUri: z.string().optional(),
          nextChunkResumableUploadSessionUri: z.string().optional(),
        }),
      },
    },
    /**
     * Sets up an appointment for manual charting mode
     */
    startAppointmentInManualChartingMode: {
      method: 'POST',
      path: ':appointmentId/manual-mode',
      body: z.object({
        duplicatedAppointmentId: z.string().optional(),
        userLocale: z.string().optional(),
      }),
      responses: {
        200: z.object({}),
      },
    },
    /**
     * Generate billing code recommendations for an appointment in manual charting mode
     * using the latest human output instead of transcripts
     */
    generateBillingCodes: {
      method: 'POST',
      path: ':appointmentId/billing-codes/generate',
      body: z.object({}),
      responses: {
        200: z.object({}),
        422: z.object({
          message: z.string(),
        }),
      },
    },
    /**
     * Redirects to a presigned GCS Url for downloading an appointment file
     */
    downloadAppointmentFile: {
      method: 'GET',
      path: ':appointmentId/download/:fileId',
      responses: {
        302: z.void(),
        404: z.void(),
      },
    },
    sendEmail: {
      method: 'POST',
      path: ':appointmentId/send-email',
      responses: {
        200: z.object({
          emailId: z.string(),
        }),
      },
      body: z.object({
        type: z.nativeEnum(OutgoingEmailTemplateTypesLookup),
        email: z.string().email(),
        subject: z.string(),
        message: z.string(),
      }),
      summary: 'Sends various appointment related emails to patient',
    },
  },
  {
    pathPrefix: '/:tenantId/appointments/',
  },
);
